import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, Car, CheckCircle2, CalendarClock, Wrench, BadgeCheck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { loansApi } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const STATUS_TONE = (label = '') => {
  const l = label.toLowerCase();
  if (l.includes('published')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (l.includes('completed')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (l.includes('started') || l.includes('progress')) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (l.includes('cancel') || l.includes('refund')) return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

/** Every car this customer had inspected — and which one goes for the loan. */
export const InspectionStatusModal = ({ open, onOpenChange, lead, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null);
  const [serverCar, setServerCar] = useState(null);

  const load = useCallback(async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const res = await loansApi.getInspections(lead.id);
      setRows(res.data.inspections || []);
      setPicked(res.data.loan_car_number || null);
      setServerCar(res.data.loan_car_number || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load inspections');
    } finally {
      setLoading(false);
    }
  }, [lead?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async () => {
    setSaving(true);
    try {
      await loansApi.setLoanCar(lead.id, picked);
      toast.success(picked ? `${picked} will be processed for the loan` : 'Loan car cleared');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the loan car');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="inspection-status-modal">
        <DialogHeader>
          <DialogTitle>Inspections · {lead?.customer_name}</DialogTitle>
          <DialogDescription>
            {rows.length === 0
              ? 'Nothing inspected for this customer yet.'
              : rows.length === 1
                ? 'One car inspected.'
                : `${rows.length} cars inspected — pick the one the loan is for.`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-2 max-h-[52vh] overflow-y-auto py-1">
            {rows.length === 0 && (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="no-inspections-msg">
                No inspection records found for this customer.
              </p>
            )}
            {rows.map((r) => {
              const active = picked && r.car_number === picked;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setPicked(active ? null : r.car_number)}
                  disabled={!r.car_number}
                  data-testid={`inspection-row-${r.car_number || r.id}`}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    active ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <Car className="h-3.5 w-3.5 text-slate-400" />
                        {r.car_number || 'Car number pending'}
                        {active && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 rounded px-1.5 py-0.5"
                            data-testid={`loan-car-badge-${r.car_number}`}>
                            Loan car
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 truncate">
                        {[r.car_make, r.car_model, r.car_year].filter(Boolean).join(' · ') || '—'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-3">
                        <span className="flex items-center gap-1">
                          <CalendarClock className="h-3 w-3" />
                          {r.scheduled_date ? `${formatDate(r.scheduled_date)}${r.scheduled_time ? ` ${r.scheduled_time}` : ''}` : 'Not scheduled'}
                        </span>
                        {r.mechanic_name && (
                          <span className="flex items-center gap-1"><Wrench className="h-3 w-3" />{r.mechanic_name}</span>
                        )}
                        {r.package_type && (
                          <span className="flex items-center gap-1"><BadgeCheck className="h-3 w-3" />{r.package_type.trim()}</span>
                        )}
                      </p>
                    </div>
                    <span className={`shrink-0 text-[11px] font-semibold rounded-full border px-2.5 py-1 ${STATUS_TONE(r.status_label)}`}
                      data-testid={`inspection-status-${r.car_number || r.id}`}>
                      {r.status_label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="inspection-modal-close">
            Close
          </Button>
          <Button onClick={save} disabled={saving || loading || (!picked && !serverCar)} data-testid="save-loan-car-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            {picked ? `Process ${picked}` : (serverCar ? 'Clear loan car' : 'Select a car')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InspectionStatusModal;
